import { useLocation, useParams } from 'react-router';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { navigate } from '@/providers';
import { AppRoutes } from '@/constants';
import { Alert, getDirtyFields, handleServerValidationErrors } from '@/lib/ui';

import {
  Badge,
  BorderedButtonGroup,
  Button,
  GlassContainer,
  Input,
  Label,
  Loading,
  MultiSelect,
  PromptMessage,
} from '@/components/common';
import { ChevronLeftIcon, Hourglass03Icon, InfoCircleIcon } from '@/assets/icons';

import { UserStatus } from '@/features/user-management/enums';
import { getUserStatusLabel } from '@/features/user-management/utils';

import { ROLE_OPTIONS } from '@/features/user-management/constants';

import { IUserUpdate } from '@/features/user-management/user-management-update/interfaces';
import {
  USER_UPDATE_FORM_FIELDS,
  USER_UPDATE_SCHEMA,
} from '@/features/user-management/user-management-update/constants';
import {
  useUserDetailsQuery,
  useUserUpdateMutation,
} from '@/features/user-management/user-management-update/hooks';
import { useCurrentProfile } from '@/hooks';
import { useEffect, useMemo } from 'react';
import { useBreadcrumbStore } from '@/stores';

const UserManagementUpdate = () => {
  const { id } = useParams();
  const location = useLocation();
  const { data: currentProfile, isFetching: isLoadingCurrentProfile } = useCurrentProfile();
  const mutation = useUserUpdateMutation(id || '');
  const { data, isFetching, error } = useUserDetailsQuery(id || '');
  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors, dirtyFields },
  } = useForm<IUserUpdate>({
    resolver: yupResolver(USER_UPDATE_SCHEMA),
    values: data as IUserUpdate,
  });

  const isViewOnly = useMemo(() => currentProfile?.id == id, [currentProfile?.id, id]);

  const onSubmit = async (data: IUserUpdate) => {
    const changedData = getDirtyFields(data, dirtyFields);
    await mutation.mutateAsync(changedData as IUserUpdate, {
      onSuccess: () => {
        navigate(AppRoutes.USER_MANAGEMENT);
        Alert.success('User details updated successfully.');
      },
      onError: (error) => {
        handleServerValidationErrors(error, setError);
      },
    });
    navigate(AppRoutes.USER_MANAGEMENT);
  };

  const { setBreadcrumbExtras, clearBreadcrumbExtras } = useBreadcrumbStore();

  useEffect(() => {
    if (id) {
      setBreadcrumbExtras({
        labelOverrides: {
          [AppRoutes.USER_MANAGEMENT_UPDATE.replace(':id', id)]: id,
        },
      });
    }
    return () => clearBreadcrumbExtras();
  }, [id, clearBreadcrumbExtras, setBreadcrumbExtras]);

  if (error) {
    navigate(AppRoutes.INTERNAL_SERVER_ERROR, { replace: true });
    return null;
  }

  if (isFetching || isLoadingCurrentProfile) {
    return <Loading fullScreen />;
  }

  if (!data) {
    navigate(AppRoutes.NOT_FOUND, { replace: true });
    return null;
  }

  return (
    <div>
      <div className="pb-3 flex flex-col gap-4">
        <Button
          buttonColor="neutral"
          buttonStyle="textlink"
          className="!text-neutral-7 self-start"
          prefixIcon={<ChevronLeftIcon />}
          onClick={() => {
            const hasHistory = location.key !== 'default';
            if (hasHistory) {
              window.history.back();
            } else {
              navigate(AppRoutes.USER_MANAGEMENT);
            }
          }}
        >
          Back to User Management
        </Button>
        <div className="text-display font-bold">User Details</div>
      </div>
      <div className="py-3">
        <div className="flex flex-col gap-4">
          {isViewOnly && (
            <PromptMessage icon={InfoCircleIcon} message="You cannot edit your own user details." />
          )}
          <GlassContainer className="p-6 flex flex-col gap-4">
            <Input
              label="User Name"
              required
              placeholder="Enter"
              error={errors[USER_UPDATE_FORM_FIELDS.fullName]?.message}
              {...register(USER_UPDATE_FORM_FIELDS.fullName)}
              disabled={isViewOnly}
            />
            <Input label="Email" required disabled value={data?.email} />
            <Controller
              name={USER_UPDATE_FORM_FIELDS.roles}
              control={control}
              render={({ field }) => (
                <MultiSelect
                  label="Role"
                  required
                  placeholder="Select"
                  options={ROLE_OPTIONS}
                  maxCount={-1}
                  defaultValue={field.value}
                  onValueChange={field.onChange}
                  error={errors[USER_UPDATE_FORM_FIELDS.roles]?.message}
                  disabled={isViewOnly}
                />
              )}
            />
            {data.status === UserStatus.Pending ? (
              <div className="flex flex-col gap-2">
                <Label required>Status</Label>
                <Badge
                  className="items-center gap-2"
                  mode="light"
                  size="default"
                  border
                  variant="yellow"
                >
                  <span>
                    <Hourglass03Icon className="size-4" />
                  </span>
                  Pending
                </Badge>
              </div>
            ) : (
              <Controller
                name={USER_UPDATE_FORM_FIELDS.status}
                control={control}
                render={({ field }) => (
                  <BorderedButtonGroup
                    buttonNames={[
                      {
                        name: getUserStatusLabel(UserStatus.Active),
                        value: UserStatus.Active,
                      },
                      {
                        name: getUserStatusLabel(UserStatus.Inactive),
                        value: UserStatus.Inactive,
                      },
                    ]}
                    selectedValue={field.value as UserStatus}
                    onSelect={(index) => field.onChange(index)}
                    required
                    label="Status"
                    buttonClass="w-[120px]"
                    error={errors[USER_UPDATE_FORM_FIELDS.status]?.message}
                    disabled={isViewOnly}
                  />
                )}
              />
            )}
          </GlassContainer>
        </div>
      </div>
      <div className="flex justify-end gap-3">
        <Button
          className="min-w-[104px]"
          buttonColor="neutral"
          buttonStyle="outline"
          borderRadius="soft"
          size="lg"
          onClick={() => {
            navigate(AppRoutes.USER_MANAGEMENT);
          }}
        >
          Cancel
        </Button>
        <Button
          className="min-w-[104px]"
          buttonColor="primary"
          buttonStyle="filled"
          borderRadius="soft"
          size="lg"
          onClick={handleSubmit(onSubmit)}
          disabled={isViewOnly}
        >
          Save
        </Button>
      </div>
      {mutation.isPending && <Loading fullScreen />}
    </div>
  );
};

export default UserManagementUpdate;
